import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { raygunHttpError } from '@approot/app.raygun.setup';
import { FX_API_SUB_PATHS, apiURL } from '@approot/utilities';
import * as Auth from 'aws-amplify/auth';
import { isEqual } from 'lodash';
import { Once } from 'lodash-decorators';
import { Observable, firstValueFrom, from, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { PhoenixEventBusService } from '../phoenix/phoenix-event-bus.service';

const MAX_CACHE_AGE = 500;
const REFRESH_INTERVAL = 5 * 60 * 1000;
const AUTH_ENDPOINT = apiURL('/exotics/exoticsssoauth');
export const SSO_SIGNOUT_KEYS = [
  'isSSO',
  'ssoPortalAction',
  'ssoPortalURL',
  'brand'
];

export type UserSession = {
  getIdToken: () => PayloadFn;
  isSSO?: boolean;
};

export type AuthPayload = {
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
  groups: string[];
  error?: string;
};

type Identity = { providerName: string };

type FxPath = FX_API_SUB_PATHS;

export type Token = {
  exp: number;
  iat: number;
  aud: string;
  identities: Identity[];
  'cognito:groups': string[];
  'cognito:username': string;
  sub: string;
  email: string;
  phone_number: string;
};

type PayloadFn = {
  getJwtToken: () => string;
  payload: Token;
};

const SSO_STATE = 'ssoState';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private cachedSession: UserSession;
  private lastCachedAt = 0;
  private _authPayload: AuthPayload;
  private refreshInterval: any;
  public readonly logoutSignal = signal<boolean>(false);
  public phxQueue = inject(PhoenixEventBusService);
  public enforceEntityRedaction: boolean = false;

  public constructor(private http: HttpClient) {
    this.load();
    if (this._authPayload) {
      this.refresh();
      this.autoRefresh();
    }

    effect(async () => {
      if (!this.logoutSignal()) {
        return;
      }
      try {
        await this.signOut();
        console.log('Logged out from Cognito');
      } catch (err) {
        console.error('Cognito logout', err);
      }
      location.href = '/';
    });
  }

  public get authPayload(): AuthPayload {
    return this._authPayload;
  }

  public set authPayload(payload: AuthPayload) {
    if (!payload?.id_token) {
      return;
    }
    this.setAuthPayload(payload);
    this.save();
  }

  public get groups(): string[] {
    return this.authPayload?.groups || [];
  }

  public get providerName() {
    return this.idTokenPayload?.identities?.[0].providerName;
  }

  private get clientId() {
    return this.idTokenPayload?.aud;
  }

  public setEnforceEntityRedaction(enforceEntityRedaction: boolean) {
    this.enforceEntityRedaction = enforceEntityRedaction;
  }

  public logout() {
    this.logAccessEvent();
    // Need time for the above event to be sent
    setTimeout(() => this.logoutSignal.set(true), 500);
  }

  @Once()
  public logAccessEvent() {
    this.phxQueue.addEvent({ type: 'access', category: 'logout' });
  }

  public async getJwtToken(): Promise<string> {
    return (await this.currentSession()).getIdToken().getJwtToken();
  }

  public currentSession$(): Observable<UserSession> {
    return from(this.currentSession());
  }

  /**
   * Consider using Observable `currentSession$()` instead
   */
  public async currentSession(): Promise<UserSession> {
    const now = Date.now();
    // Cognito seems to barf if called too often, so we cache the session for a bit
    if (this.cachedSession && now - this.lastCachedAt < MAX_CACHE_AGE) {
      return this.cachedSession;
    }

    if (this._authPayload) {
      const token = this._authPayload.id_token;
      const payload = cleanCognitoGroups(parseJWT(token));
      this.cachedSession = {
        getIdToken: () => {
          return {
            getJwtToken: () => token,
            payload
          };
        },
        isSSO: true
      };
    } else {
      const fetchedAuthSession = await Auth.fetchAuthSession();
      this.cachedSession = {
        getIdToken: () => {
          return {
            getJwtToken: () => fetchedAuthSession.tokens.idToken.toString(),
            payload: fetchedAuthSession.tokens.idToken.payload as Token
          };
        }
      };
    }
    this.lastCachedAt = now;
    return this.cachedSession;
  }

  public async exchangeCode(
    code: string,
    clientId: string
  ): Promise<AuthPayload> {
    return firstValueFrom(
      this.http
        .post<AuthPayload>(AUTH_ENDPOINT, {
          mode: 'exchangeCode',
          code,
          clientId,
          uri: buildURL(clientId)
        })
        .pipe(
          tap(response => {
            if (response.refresh_token) {
              this.autoRefresh();
            }
          })
        )
    );
  }

  public async refresh(): Promise<AuthPayload> {
    const refreshToken = this.authPayload?.refresh_token;
    if (!refreshToken) {
      return null;
    }
    return firstValueFrom(
      this.http
        .post<AuthPayload>(AUTH_ENDPOINT, {
          mode: 'refresh',
          refreshToken,
          clientId: this.clientId
        })
        .pipe(
          tap(tokens => {
            this.setAuthPayload({
              ...this.authPayload,
              ...tokens
            });
          })
        )
    );
  }

  public fxPost<T>(
    payload: any,
    path: FxPath,
    params: string | Record<string, string>,
    options: { logRequestResponse?: boolean; logError?: boolean } = {}
  ): Observable<T> {
    const urlPath = `/fx/${path}`;
    options = { logRequestResponse: true, logError: true, ...options };
    if (options.logRequestResponse) {
      this.phxQueue.addEvent({
        type: 'hydra',
        category: 'request',
        urlPath,
        payload
      });
    }

    return this.doPost<T>(payload, path, params).pipe(
      tap(response => {
        if (options.logRequestResponse) {
          this.phxQueue.addEvent({
            type: 'hydra',
            category: 'response',
            urlPath,
            response
          });
        }
      }),
      catchError(err => {
        if (options.logError) {
          this.logHydraError(err, urlPath, payload);
        }
        raygunHttpError(err, ['fxPost']);
        return throwError(() => err);
      })
    );
  }

  // ****************PRIVATE ********************** /

  private async signOut() {
    const vals = SSO_SIGNOUT_KEYS.map(k => sessionStorage.getItem(k));
    sessionStorage.clear();
    SSO_SIGNOUT_KEYS.forEach((k, i) => {
      const v = vals[i];
      if (v != null) {
        sessionStorage.setItem(k, v);
      }
    });
    this._authPayload = null;
    this.logAccessEvent();
    await Auth.signOut();
  }

  private autoRefresh(): void {
    console.log('AuthService: initialising auto-refresh');
    try {
      clearInterval(this.refreshInterval);
      this.refreshInterval = setInterval(async () => {
        const tokens = await this.refresh();
        if (tokens) {
          console.log(
            'AutoRefreshed SSO tokens',
            tokens,
            parseJWT(tokens.id_token)
          );
        }
      }, REFRESH_INTERVAL);
    } catch (e) {
      console.warn('autoRefresh', e);
    }
  }

  private save(): void {
    const oldState = JSON.parse(sessionStorage.getItem(SSO_STATE));

    const newState = {
      authPayload: this._authPayload,
      providerName: this.providerName,
      clientId: this.clientId
    };

    if (isEqual(newState, oldState)) {
      return;
    }

    console.log('AuthService: saving state', newState);
    sessionStorage.setItem(SSO_STATE, JSON.stringify(newState));
  }

  private load(): void {
    const data = sessionStorage.getItem(SSO_STATE);
    if (!data) {
      return;
    }
    const parsed = JSON.parse(data);
    console.log('AuthService: loading state', parsed);
    const { authPayload } = parsed;
    this.setAuthPayload(authPayload, false);
  }

  private setAuthPayload(
    payload: AuthPayload,
    save = true
  ): {
    idTokenPayload: Token;
  } {
    this._authPayload = payload;
    if (save) {
      this.save();
    }
    if (!payload) {
      return null;
    }
    const idToken = parseJWT(payload.id_token);
    return { idTokenPayload: idToken };
  }

  private get idTokenPayload(): Token {
    if (!this._authPayload) {
      return null;
    }
    return parseJWT(this._authPayload.id_token);
  }

  public doPost<T>(
    payload: any,
    path: FxPath,
    params: string | Record<string, string>,
    root: string = 'fx'
  ): Observable<T> {
    let queryParams: string;
    if (typeof params === 'string') {
      queryParams = params;
    } else {
      queryParams = new HttpParams().appendAll(params).toString();
    }

    return from(this.currentSession()).pipe(
      switchMap(session => {
        return this.http.post<T>(
          `${apiURL()}/${root}/${path}?${queryParams}`,
          payload,
          getHeaders(session.getIdToken().getJwtToken())
        );
      })
    );
  }

  public logHydraError(err: any, urlPath: string, payload: any) {
    this.phxQueue.addEvent({
      errorId: undefined,
      type: 'hydra',
      category: 'error',
      urlPath,
      payload,
      error: err,
      context: { source: 'authService' }
    });
  }
}

export function getHeaders(token: string) {
  let headers = new HttpHeaders()
    .set('Content-Type', 'application/json')
    .set('Authorization', `${token}`);
  return { headers };
}

function buildURL(clientId: string): string {
  return `${location.origin}/sso/${clientId}/`;
}

/**
 * SSO users get added to a group like "eu-west-1_<poolId>_<provider>"
 * but other code assumes user is only part of one group.
 */
function cleanCognitoGroups(payload: Token): Token {
  let groups: string[] = payload['cognito:groups'] || [];
  groups = groups.filter(g => !g.startsWith('eu-west'));
  return { ...payload, 'cognito:groups': groups };
}

function parseJWT(token: string): Token | null {
  if (!token) {
    return null;
  }
  return JSON.parse(window.atob(token.split('.')[1]));
}

export async function currentAuthenticatedUser() {
  return await Auth.getCurrentUser();
}
